import { PERSONAL_INFO_FIELD_NAMES, USA } from '../../constants';
import { isSchedulingPreference } from '../health-care-settings/schedulingPreferencesUtils';

export const SUBMIT_STRATEGIES = {
  REQUIRE_FIELD: 'REQUIRE_FIELD',
  MESSAGING_SIGNATURE: 'MESSAGING_SIGNATURE',
  PERSONAL_INFO: 'PERSONAL_INFO',
  SCHEDULING_PREFERENCES: 'SCHEDULING_PREFERENCES',
  VALIDATE_ADDRESS: 'VALIDATE_ADDRESS',
  CONFIRM_INTL_MOBILE: 'CONFIRM_INTL_MOBILE',
  CREATE_TRANSACTION: 'CREATE_TRANSACTION',
};

const isAddressField = fieldName => fieldName.toLowerCase().includes('address');

const isPersonalInfoField = fieldName =>
  Object.values(PERSONAL_INFO_FIELD_NAMES).includes(fieldName);

/**
 * An international mobile number needs a confirmation step before it is saved,
 * because text notifications can't be sent to one. `countryCode == 1` is
 * flagged international by the component but is a US number, so it is skipped.
 */
const needsIntlMobileConfirmation = (fieldName, payload) =>
  fieldName === 'mobilePhone' &&
  !!payload?.isInternational &&
  String(payload?.countryCode) !== USA.COUNTRY_CODE;

/**
 * What submitting the edit view should do, as data. The caller dispatches;
 * this decides. Returns one strategy object whose `type` is a SUBMIT_STRATEGIES
 * value and whose remaining keys are that strategy's arguments.
 */
export const getSubmitStrategy = ({
  fieldName,
  field,
  apiRoute,
  analyticsSectionName,
  convertCleanDataToPayload,
}) => {
  const payload = convertCleanDataToPayload
    ? convertCleanDataToPayload(field.value, fieldName)
    : field.value;

  if (isPersonalInfoField(fieldName)) {
    // Personal information updates require a value. Rather than submitting an
    // empty one, mark the field required so the form shows its own validation
    // message — a fix for a blur validation bug.
    if (
      fieldName === PERSONAL_INFO_FIELD_NAMES.PREFERRED_NAME &&
      !field.value?.[PERSONAL_INFO_FIELD_NAMES.PREFERRED_NAME]
    ) {
      return {
        type: SUBMIT_STRATEGIES.REQUIRE_FIELD,
        value: field.value,
        // PORT NOTE (bug fix): the class assigned `required` onto
        // field.formSchema, mutating the schema object held in the Redux store
        // in place. The mutation outlived the submit, so the field stayed
        // required for the rest of the session even after the Veteran filled
        // it in and saved.
        formSchema: { ...field.formSchema, required: [fieldName] },
        uiSchema: field.uiSchema,
      };
    }

    if (fieldName === PERSONAL_INFO_FIELD_NAMES.MESSAGING_SIGNATURE) {
      return {
        type: SUBMIT_STRATEGIES.MESSAGING_SIGNATURE,
        payload,
        method: 'POST',
      };
    }

    return {
      type: SUBMIT_STRATEGIES.PERSONAL_INFO,
      request: {
        route: apiRoute,
        method: 'PUT',
        fieldName,
        payload,
        analyticsSectionName,
        value: field.value,
      },
    };
  }

  if (isSchedulingPreference(fieldName)) {
    return {
      type: SUBMIT_STRATEGIES.SCHEDULING_PREFERENCES,
      request: {
        route: apiRoute,
        method: 'POST',
        fieldName,
        payload,
        analyticsSectionName,
        value: field.value,
      },
    };
  }

  const method = payload?.id ? 'PUT' : 'POST';

  if (isAddressField(fieldName)) {
    return { type: SUBMIT_STRATEGIES.VALIDATE_ADDRESS, method, payload };
  }

  if (needsIntlMobileConfirmation(fieldName, payload)) {
    return {
      type: SUBMIT_STRATEGIES.CONFIRM_INTL_MOBILE,
      countryCode: payload.countryCode,
      phoneNumber: payload.phoneNumber,
      method,
      payload,
    };
  }

  return { type: SUBMIT_STRATEGIES.CREATE_TRANSACTION, method, payload };
};
